/**
 * Letters of a word in languages with multi-character letters (Croatian dž, lj, nj):
 * "LJUBAV" is five letters, L-J shares one tile and one step. Rules come from
 * config/letters.json, a copy of wordgame/data/letters.json (a backend test keeps them equal).
 */
import LETTER_RULES from '../config/letters.json';
import { getActiveLanguage } from '../i18n';

// Turkish (and Azerbaijani) pair i/İ and ı/I; plain toUpperCase()/toLowerCase() map i↔I.
const DOTTED_I_LANGUAGES = new Set(['tr', 'az']);

const compiled = {};

function rulesFor(language) {
    if (!compiled[language]) {
        const spec = LETTER_RULES[language] || {};
        const multigraphs = [...(spec.multigraphs || [])].sort((a, b) => b.length - a.length);
        const splits = (spec.split || []).map(pattern => {
            const anchored = pattern.startsWith('^');
            const body = anchored ? pattern.slice(1) : pattern;
            return { text: body.replace('|', ''), offset: body.indexOf('|'), anchored };
        });
        compiled[language] = { multigraphs, splits };
    }
    return compiled[language];
}

export function hasMultigraphs(language) {
    return rulesFor(language).multigraphs.length > 0;
}

function boundaries(lower, splits) {
    const cuts = new Set();
    for (const { text, offset, anchored } of splits) {
        let start = 0;
        for (;;) {
            const at = lower.indexOf(text, start);
            if (at < 0 || (anchored && at > 0)) break;
            cuts.add(at + offset);
            start = at + 1;
        }
    }
    return cuts;
}

/** 'LJUBAV' -> ['LJ', 'U', 'B', 'A', 'V'] in Croatian; case is kept, matching ignores case. */
export function splitLetters(word, language) {
    const text = word || '';
    const { multigraphs, splits } = rulesFor(language);
    if (multigraphs.length === 0) return Array.from(text);
    const lower = lowerWord(text, language);
    if (lower.length !== text.length) return Array.from(text);
    const cuts = boundaries(lower, splits);
    const letters = [];
    let i = 0;
    while (i < text.length) {
        let taken = false;
        for (const m of multigraphs) {
            if (lower.startsWith(m, i)) {
                let crossesCut = false;
                for (let c = i + 1; c < i + m.length; c++) if (cuts.has(c)) crossesCut = true;
                if (!crossesCut) {
                    letters.push(text.slice(i, i + m.length));
                    i += m.length;
                    taken = true;
                    break;
                }
            }
        }
        if (!taken) {
            letters.push(text[i]);
            i += 1;
        }
    }
    return letters;
}

export function letterCount(word, language) {
    return splitLetters(word, language).length;
}

/** The longest prefix of `word` that has at most `max` letters. */
export function clampLetters(word, max, language) {
    const letters = splitLetters(word, language);
    return letters.length > max ? letters.slice(0, max).join('') : word;
}

const folds = {};

/** The spelling the dictionary stores: Russian ЁЛКА -> ЕЛКА (ё and е are one letter). */
export function foldLetters(word, language) {
    if (!folds[language]) {
        const fold = (LETTER_RULES[language] || {}).fold || {};
        folds[language] = {};
        for (const [from, to] of Object.entries(fold)) {
            folds[language][from] = to;
            folds[language][from.toUpperCase()] = to.toUpperCase();
        }
    }
    const map = folds[language];
    if (!word || Object.keys(map).length === 0) return word || '';
    return Array.from(word, c => map[c] || c).join('');
}

/** Uppercase a word letter for letter: ß becomes ẞ, not "SS"; Turkish i becomes İ. */
export function upperWord(word, language = getActiveLanguage()) {
    let text = (word || '').replace(/ß/g, 'ẞ');
    if (DOTTED_I_LANGUAGES.has(language)) text = text.replace(/i/g, 'İ').replace(/ı/g, 'I');
    return text.toUpperCase();
}

/** Lowercase a word letter for letter: Turkish İ becomes i (not i + combining dot) and I becomes ı. */
export function lowerWord(word, language = getActiveLanguage()) {
    let text = word || '';
    if (DOTTED_I_LANGUAGES.has(language)) text = text.replace(/İ/g, 'i').replace(/I/g, 'ı');
    return text.toLowerCase();
}

/** "3rd" / "3." / "3.ª": the ordinal used when talking about a letter position in this language. */
export function ordinal(n, language) {
    if (language === 'id') return `ke-${n}`;
    if (language === 'fr') return n === 1 ? '1re' : `${n}e`;
    if (language === 'es') return `${n}.ª`;
    if (language === 'ru') return `${n}-я`;
    if (language === 'it' || language === 'pt') return `${n}ª`;
    if (language !== 'en') return `${n}.`;
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
